import Papa from "papaparse";
import { supabase } from "~/integrations/supabase/client";
import type {
  AccountMappingCSVRow,
  AccountMappingImportResult,
} from "~/types/contabilidad";

interface ValidationError {
  row: number;
  error: string;
}

interface ValidationResult {
  valid: boolean;
  errors: ValidationError[];
  validRows: AccountMappingCSVRow[];
}

export const accountMappingImportService = {
  /**
   * Parse CSV file (semicolon-delimited)
   */
  async parseCSV(file: File): Promise<AccountMappingCSVRow[]> {
    return new Promise((resolve, reject) => {
      Papa.parse<Record<string, string>>(file, {
        header: true,
        delimiter: ";",
        skipEmptyLines: true,
        complete: (results) => {
          try {
            const rows: AccountMappingCSVRow[] = results.data.map((row) => ({
              codigo: row["Codigo"]?.trim() || "",
              cuenta: row["Cuenta"]?.trim() || "",
              tipo: row["Tipo"]?.trim() || "",
              reporte: row["Reporte"]?.trim() || "",
              clase: row["Clase"]?.trim() || "",
              orden_clase: parseInt(row["Orden Clase"]) || 0,
              orden_tipo: parseInt(row["Orden Tipo"]) || 0,
              sub_clase: row["Sub Clase"]?.trim() || "",
              orden_subclase: parseInt(row["Orden Subclase"]) || 0,
              sub_clase_2: row["SubClase 2"]?.trim() || "",
              orden_subclase_2: parseInt(row["Orden Subclase 2"]) || 0,
              grupo_cuenta: row["Grupo Cuenta"]?.trim() || "",
              orden_grupo_cuenta: parseInt(row["Orden grupo cta."]) || 0,
              clave: row["Clave"]?.trim() || "",
              signo: parseInt(row["Signo"]) || -1,
              clase_1: row["Clase 1"]?.trim() || "",
            }));
            resolve(rows);
          } catch (error) {
            reject(
              new Error(
                `Error parsing CSV: ${error instanceof Error ? error.message : "Unknown error"}`
              )
            );
          }
        },
        error: (error) => {
          reject(new Error(`CSV parsing error: ${error.message}`));
        },
      });
    });
  },

  /**
   * Validate parsed mappings
   */
  async validateMappings(
    rows: AccountMappingCSVRow[],
    companyId: string
  ): Promise<ValidationResult> {
    const errors: ValidationError[] = [];
    const validRows: AccountMappingCSVRow[] = [];

    // Fetch all account codes for the company to validate
    const { data: accounts, error: accountsError } = await supabase
      .from("account")
      .select("code")
      .eq("company_id", companyId)
      .eq("is_deleted", false);

    if (accountsError) {
      throw new Error(`Failed to fetch accounts: ${accountsError.message}`);
    }

    const accountCodes = new Set(accounts?.map((a) => a.code) || []);
    const seenCodes = new Set<string>();

    rows.forEach((row, index) => {
      const rowNumber = index + 2; // +2 because CSV has header row and is 1-indexed

      // Validate required fields
      if (!row.codigo) {
        errors.push({ row: rowNumber, error: "Missing account code" });
        return;
      }

      if (!row.cuenta) {
        errors.push({ row: rowNumber, error: "Missing account name" });
        return;
      }

      if (!row.clase) {
        errors.push({ row: rowNumber, error: "Missing clase" });
        return;
      }

      // Check if account exists in the database
      if (!accountCodes.has(row.codigo)) {
        errors.push({
          row: rowNumber,
          error: `Account ${row.codigo} not found in database`,
        });
        return;
      }

      // Check for duplicates in the CSV
      if (seenCodes.has(row.codigo)) {
        errors.push({
          row: rowNumber,
          error: `Duplicate account code ${row.codigo}`,
        });
        return;
      }

      seenCodes.add(row.codigo);
      validRows.push(row);
    });

    return {
      valid: errors.length === 0,
      errors,
      validRows,
    };
  },

  /**
   * Import mappings into database
   */
  async importMappings(
    rows: AccountMappingCSVRow[],
    templateName: string,
    companyId: string
  ): Promise<AccountMappingImportResult> {
    try {
      // 1. Create or find mapping template
      let templateId: string;

      const { data: existingTemplate } = await supabase
        .from("account_mapping_template")
        .select("id")
        .eq("company_id", companyId)
        .eq("name", templateName)
        .single();

      if (existingTemplate) {
        templateId = existingTemplate.id;

        // Delete existing mappings for this template
        await supabase
          .from("account_mapping")
          .delete()
          .eq("template_id", templateId);
      } else {
        // Create new template
        const { data: newTemplate, error: templateError } = await supabase
          .from("account_mapping_template")
          .insert({
            name: templateName,
            company_id: companyId,
            description: `Imported from CSV on ${new Date().toLocaleDateString()}`,
          })
          .select()
          .single();

        if (templateError) {
          throw new Error(`Failed to create template: ${templateError.message}`);
        }

        templateId = newTemplate.id;
      }

      // 2. Fetch account IDs for the codes
      const codes = rows.map((r) => r.codigo);
      const { data: accounts, error: accountsError } = await supabase
        .from("account")
        .select("id, code")
        .eq("company_id", companyId)
        .in("code", codes);

      if (accountsError) {
        throw new Error(`Failed to fetch accounts: ${accountsError.message}`);
      }

      const codeToIdMap = new Map(accounts?.map((a) => [a.code, a.id]) || []);

      // 3. Prepare mappings for bulk insert
      const mappings = rows
        .map((row) => {
          const accountId = codeToIdMap.get(row.codigo);
          if (!accountId) return null;

          return {
            template_id: templateId,
            account_id: accountId,
            reporte: row.reporte,
            clase: row.clase,
            orden_clase: row.orden_clase,
            tipo: row.tipo || null,
            orden_tipo: row.orden_tipo || null,
            sub_clase: row.sub_clase || null,
            orden_subclase: row.orden_subclase || null,
            sub_clase_2: row.sub_clase_2 || null,
            orden_subclase_2: row.orden_subclase_2 || null,
            grupo_cuenta_id: null, // Will need to resolve this separately if needed
            orden_grupo_cuenta: row.orden_grupo_cuenta || null,
            clave: row.clave || null,
            signo: row.signo,
          };
        })
        .filter((m) => m !== null);

      // 4. Bulk insert
      const { error: insertError } = await supabase
        .from("account_mapping")
        .insert(mappings);

      if (insertError) {
        throw new Error(`Failed to insert mappings: ${insertError.message}`);
      }

      return {
        success: true,
        imported: mappings.length,
        errors: [],
        skipped: rows.length - mappings.length,
      };
    } catch (error) {
      return {
        success: false,
        imported: 0,
        errors: [
          {
            row: 0,
            error:
              error instanceof Error ? error.message : "Unknown import error",
          },
        ],
        skipped: 0,
      };
    }
  },
};
